import React, { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { isEqual } from 'lodash';
import { PlusOutlined } from '@ant-design/icons';

import {
  addGuarBeneficiary,
  getGuarBeneficiaryList,
  GuarBeneficiaryResponse,
  removeGuarBeneficiary,
  updateGuarBeneficiary,
} from 'services/guaranty';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { useFormList } from 'hooks/useFormList';
import { Col, DatePicker, Form, notification, Popconfirm, Row, Tag } from 'antd';
import { indexBy, toDatePickerDate } from 'utils/utils2';
import ModalController from 'components/ModalController';
import ThirdPartyModal from 'pages/ThirdParty/ThirdPartyDetail/ThirdPartyModal';
import ThirdPartyInput from 'components/ThirdPartyInput';
import DeleteButton from 'components/DeleteButton';
import GarantyStatusButton from './ButtonGarantyStatus';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ButtonOperation from 'components/ButtonOperation';
import ISave from 'icons/ISave';
import InputCurrency from 'components/InputCurrency';
import {
  CheckCircleOutlined
} from '@ant-design/icons';
import SaveButtonIcon from 'components/SaveBtn';

type Values = {
  guaranty: GuarBeneficiaryResponse[];
  
};
const NEW_GUAR_ID = -1;

const Beneficiary = (props: { hideTitle?:any;currcode: string; guaramount: number, guarreceptiondate: string, refetch1: () => void, guarId?: string, iscreate?: boolean }) => {

  const { guaramount, guarreceptiondate, currcode, refetch1, guarId, iscreate,hideTitle } = props;
  //const { idOrCreate } = useParams<{ idOrCreate: string }>();
  const [form] = Form.useForm();
  const fieldName = 'guaranty';




  const { data, refetch, isFetching } = useQuery(
    ['getGuarBeneficiaryList', { guarid: Number(guarId) }],
    () =>
      getGuarBeneficiaryList({ guarid: Number(guarId) }).then(
        (res) => res.list || []
      )
  );

  const { remove, add } = useFormList(form, fieldName, {
    idKey: 'guarassocid',
    addAsFirst: true,
  });

  useEffect(() => {
    refetch1();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data.map((da) => {
          return {
            ...da,
            startdate: toDatePickerDate(da.startdate),
            enddate: toDatePickerDate(da.enddate)
          }
        })
      })

    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
    refetch()
  }, [form, data,refetch]);

  const onSaveOrEdit = useCallback(
    (record: GuarBeneficiaryResponse) => {
      const deprecations: GuarBeneficiaryResponse[] = form.getFieldValue(
        fieldName
      );
      const index = indexBy(deprecations, 'guarassocid')[record.guarassocid];
      const currentValue = {
        ...deprecations[index],
      };
      const dataTosave = {
        enddate: currentValue.enddate!=null ? currentValue.enddate : currentValue.guarstatusdate,
        guarassocid: currentValue.guarassocid,
        guarid: currentValue.guarid,
        guarusageamount: currentValue.guarusageamount,
        startdate: currentValue.startdate ?? toDatePickerDate(guarreceptiondate),
        tpidbeneficiary: currentValue.tpidbeneficiary,
        tpname:currentValue.tpname,
        tpreference:currentValue.tpreference,
      };
      dataTosave.guarassocid === NEW_GUAR_ID
        ? addGuarBeneficiary({
          guarid: currentValue.guarid,
          benef: dataTosave,
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });
            refetch();
          }
        })
        : updateGuarBeneficiary({
          gassocid: currentValue.guarassocid,
          guarid: currentValue.guarid,
          benef: dataTosave,
        }).then((response) => {
          if (response.code === '200') {
            notification.success({
              message: response.message,
            });
            refetch();
          }
        });

    },
    [form, refetch]
  );

  const getName = useCallback(
    (name: string) => (record: GuarBeneficiaryResponse) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'guarassocid')[record.guarassocid];

      return [fieldName, index, name];
    },
    [form]
  );


  const columns = useMemo<DataGridColDef<GuarBeneficiaryResponse>[]>(
    () => [
      {
        dataIndex: 'tpidbeneficiary',
        width: '20%',
        render: (value, record) => {
          return record.guarassocid === NEW_GUAR_ID ? (
            <Form.Item name="tpidbeneficiary" rules={[{ required: true, message: <GetLabel label="tpidbeneficiary_error_msg" /> }]}>
              <ThirdPartyInput
                style={{ marginTop: '5px' }}
                role=""
                onChange={(e) => {
                  record.tpidbeneficiary = e.tpid;
                  record.tpname= e.tpname;
                  record.tpreference = e.tpreference;
                }}
              />
            </Form.Item>
          ) : (
            <ModalController>
              {({ setVisible, visible }) => (
                <>
                  <a
                    onClick={() => {
                      setVisible(true);
                    }}
                  >
                    {record.tpreference + ' - ' + record.tpname}
                  </a>
                  <ThirdPartyModal
                    tpId={record.tpidbeneficiary}
                    visible={visible}
                    onCancel={() => setVisible(false)}
                    onSuccess={() => setVisible(false)}
                    title={record.tpname}
                    iscreate={false}
                  />
                </>
              )}
            </ModalController>
          );
        },
      },
      {
        dataIndex: 'guarusageamount',
        width: 100,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('guarusageamount')(record)}>
              <InputCurrency max={guaramount} style={{ width: '100% !important' }} currcode={currcode} />
            </Form.Item>
          )
        },
      },
      {
        dataIndex: 'startdate',
        width: 140,

        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('startdate')(record)}>
              <DatePicker />
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'enddate',

        width: 140,

        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('enddate')(record)}>
              <DatePicker />
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'statuslabel',
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('statuslabel')(record)}>
              <Tag icon={record.guarstatus === 'ACTIVE' && <CheckCircleOutlined />} color={colorTag(record.guarstatus)}>
                {value}
              </Tag>
            </Form.Item>
          );
        },
        width: 100,
      },
      {
        comcode: 'action',
        fixed: 'right',
        width: 150,
        align: 'center',
        render: (value, record) => {
          return ((iscreate || iscreate === undefined) ?
            record.guarassocid === NEW_GUAR_ID ? (
              <Row justify='center'>
                <Col span={21}>
                  <ButtonOperation disabled={record.tpidbeneficiary == null  || record.startdate === ''||  record.guarusageamount===null} icon={<ISave color="white" />} type="primary" onClick={(ev) => { ev.preventDefault(); onSaveOrEdit(record); }} size="small" comcode="save" />
                  <DeleteButton onClick={() => remove(record.guarassocid)} onlyIcon />
                </Col>
              </Row>
            ) : (
              record.guarstatus === 'INIT' ? (
                <>
                  <GarantyStatusButton
                    refetch={refetch} guarid={record.guarid} guarassocid={record.guarassocid} status='INIT'
                  />
                  <SaveButtonIcon
                    onClick={(ev: any) => {
                      ev.preventDefault();
                      onSaveOrEdit(record);
                    }}
                    onlyIcon
                  />

                  <Popconfirm
                    title={<GetLabel label="msg_confirm_delete_benf" />}
                    onConfirm={() => removeGuarBeneficiary({ gassocid: record.guarassocid, guarid: record.guarid }).then((res) => {
                      if (res.code === '200') {
                        notification.success({ message: res.message });
                        refetch();
                      } else {
                        notification.error({ message: res.message });
                      }
                    })}
                  >
                    <DeleteButton onlyIcon />

                  </Popconfirm>
                </>
              ) : record.guarstatus === 'ACTIVE' ? (
                <>
                  <GarantyStatusButton refetch={refetch} guarid={record.guarid} guarassocid={record.guarassocid} status='ACTIVE' />

                  <SaveButtonIcon
                    onClick={(ev: any) => {
                      ev.preventDefault();
                      onSaveOrEdit(record);
                    }}
                    onlyIcon
                  />

                  <Popconfirm
                    title={<GetLabel label="msg_confirm_delete_benf" />}
                    onConfirm={() => removeGuarBeneficiary({ gassocid: record.guarassocid, guarid: record.guarid }).then((res) => {
                      if (res.code === '200') {
                        notification.success({ message: res.message });
                        refetch();
                      } else {
                        notification.error({ message: res.message });
                      }
                    })}
                  >
                    <DeleteButton onlyIcon />

                  </Popconfirm>
                </>
              ) : record.guarstatus === 'DISABLED' ? (
                <>
                  <GarantyStatusButton refetch={refetch} guarid={record.guarid} guarassocid={record.guarassocid} status='DISABLED' />

                  <SaveButtonIcon
                    onClick={(ev: any) => {
                      ev.preventDefault();
                      onSaveOrEdit(record);
                    }}
                    onlyIcon
                  />

                  <Popconfirm
                    title={<GetLabel label="msg_confirm_delete_benf" />}
                    onConfirm={() => removeGuarBeneficiary({ gassocid: record.guarassocid, guarid: record.guarid }).then((res) => {
                      if (res.code === '200') {
                        notification.success({ message: res.message });
                        refetch();
                      } else {
                        notification.error({ message: res.message });
                      }
                    })}
                  >
                    <DeleteButton onlyIcon />

                  </Popconfirm>
                </>
              ) : (
                <>
                  <GarantyStatusButton refetch={refetch} guarid={record.guarid} guarassocid={record.guarassocid} status='TERMINATED' />
                  <SaveButtonIcon
                    onClick={(ev: any) => {
                      ev.preventDefault();
                      onSaveOrEdit(record);
                    }}
                    onlyIcon
                  />

                  <Popconfirm
                    title={<GetLabel label="msg_confirm_delete_benf" />}
                    onConfirm={() => removeGuarBeneficiary({ gassocid: record.guarassocid, guarid: record.guarid }).then((res) => {
                      if (res.code === '200') {
                        notification.success({ message: res.message });
                        refetch();
                      } else {
                        notification.error({ message: res.message });
                      }
                    })}
                  >
                    <DeleteButton onlyIcon />

                  </Popconfirm>
                </>
              )
            ) : ''

          )
        }
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getName, onSaveOrEdit, remove,refetch]
  );





  const addRow = () => {
    add(row);
  };

  const row: any = {
    enddate: '',
    guarassocid: NEW_GUAR_ID,
    guarid: Number(guarId),
    guarstatus: '',
    guarstatusdate: '',
    guarusageamount: null,
    startdate: '',
    statuslabel: '',
    tpidbeneficiary: null,
    tpname: '',
    tpreference: '',
  };

  const colorTag = (status: string) => {
    if (status === '') return 'white'
    if (status === 'ACTIVE') {
      return 'success'
    } else if (status === 'INIT') {
      return 'default'
    } else if (status === 'DISABLED') {
      return 'red'
    } else if (status === 'TERMINATED') {
      return 'geekblue'
    }
    return ''
  }

  return (
    <Form form={form} initialValues={row}>
      <Form.Item
        noStyle
        shouldUpdate={(prevValue: Values, nextValue: Values) => {
          return !isEqual(prevValue, nextValue);
        }}
      >
        {({ getFieldValue }) => {
          const Guaranties: GuarBeneficiaryResponse[] = getFieldValue(
            fieldName
          );
          const isNewItemInTable =
            Guaranties?.find(
              ({ guarassocid }) => guarassocid === NEW_GUAR_ID
            ) !== undefined;
          return (
            <>
              <Row justify='end'>
                {(iscreate || iscreate === undefined) && <Col style={{ display: 'flex', justifyContent: 'end' }} span={3}>
                  <ButtonOperation type="primary" icon={<PlusOutlined />} onClick={addRow} disabled={isNewItemInTable} comcode="add" />
                </Col>
                }

              </Row>
              <DataGrid
                rowKey="guarid"
                dataSource={Guaranties}
                columns={columns}
                isLoading={isFetching}
                hideFilters
                hideExtraAction
                titleComcode='beneficiary_list'
                hideTitle={hideTitle}
              />
            </>

          );
        }}
      </Form.Item>
    </Form>
  );
};

export default Beneficiary;
